import { useEffect, useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { COMPANY, WHATSAPP_PROMPT } from "../data/content";

const POPUP_KEY = "haas_wa_popup_dismissed";

function whatsappHref() {
    const text = encodeURIComponent(WHATSAPP_PROMPT.prefill);
    return `https://wa.me/${COMPANY.whatsappNumber}?text=${text}`;
}

export default function WhatsAppFloating() {
    const [popupOpen, setPopupOpen] = useState(false);

    useEffect(() => {
        // Only auto-open if not dismissed this session
        let dismissed = false;
        try {
            dismissed = sessionStorage.getItem(POPUP_KEY) === "1";
        } catch (_) {}
        if (dismissed) return;
        const t = setTimeout(() => setPopupOpen(true), 2200);
        return () => clearTimeout(t);
    }, []);

    const dismiss = () => {
        setPopupOpen(false);
        try {
            sessionStorage.setItem(POPUP_KEY, "1");
        } catch (_) {}
    };

    const openChat = () => {
        try {
            sessionStorage.setItem(POPUP_KEY, "1");
        } catch (_) {}
    };

    return (
        <>
            {/* Auto popup */}
            {popupOpen && (
                <div
                    data-testid="whatsapp-popup"
                    className="fixed z-50 bottom-24 right-4 sm:right-6 max-w-[20rem] sm:max-w-sm animate-in fade-in slide-in-from-bottom-2 duration-300"
                >
                    <div className="relative rounded-3xl bg-white border-2 border-haas-ink shadow-[8px_8px_0_#123D28] overflow-hidden">
                        <button
                            type="button"
                            onClick={dismiss}
                            data-testid="whatsapp-popup-close"
                            aria-label="Dismiss WhatsApp prompt"
                            className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-haas-sand hover:bg-haas-sun border-2 border-haas-ink text-haas-ink flex items-center justify-center"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        {/* Top bar */}
                        <div className="bg-[#25D366] text-white px-5 py-3 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                <MessageCircle className="w-5 h-5 text-[#25D366] fill-[#25D366]" />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-[0.18em] font-bold opacity-90">
                                    WhatsApp
                                </div>
                                <div className="font-display font-bold text-sm leading-tight">
                                    Luxe Artificial Grass
                                </div>
                            </div>
                        </div>

                        <div className="px-5 py-4 pr-9">
                            <div className="font-display font-bold text-base text-haas-ink leading-tight mb-2">
                                {WHATSAPP_PROMPT.title}
                            </div>
                            <p className="text-haas-soft text-sm leading-relaxed">
                                {WHATSAPP_PROMPT.message}
                            </p>

                            <a
                                href={whatsappHref()}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={openChat}
                                data-testid="whatsapp-popup-cta"
                                className="mt-4 inline-flex items-center justify-center w-full gap-2 px-5 py-3 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white font-bold text-sm transition-colors shadow-[3px_3px_0_#123D28] border-2 border-haas-ink"
                            >
                                <MessageCircle className="w-4 h-4" />
                                {WHATSAPP_PROMPT.cta}
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {/* Floating action button (always visible) */}
            <a
                href={whatsappHref()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={openChat}
                data-testid="whatsapp-fab"
                aria-label="Chat with Luxe Artificial Grass on WhatsApp"
                className="fixed z-50 bottom-5 right-4 sm:right-6 inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white border-2 border-haas-ink shadow-[4px_4px_0_#123D28] transition-transform hover:scale-105"
            >
                <MessageCircle className="w-7 h-7 sm:w-8 sm:h-8 fill-white" />
                <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-haas-coral opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-haas-coral border-2 border-white"></span>
                </span>
            </a>
        </>
    );
}
